import { Divider, List, Stack } from "@mui/material";
import { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useLocation } from "react-router";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { getDeviceRoute, getGroupRoute, getTagRoute, getTankBatteryRoute, getTankRoute, getViewIDFromPath, getWellRoute } from "../../common/utilities/RouteHelpers";
import { LayoutContextActionType } from "../../contexts/layout-context/LayoutContextTypes";
import { useLayoutContext } from "../../contexts/layout-context/UseLayoutContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useBreadcrumb } from "../../queries/breadcrumb/UseBreadcrumb";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IRoute } from "../../routes/routes";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import { IUICustomizationSettingsDTO, IUICustomizationViewCategorySettingsDTO } from "../../services/ui-customization-data-service/Models";
import { bindUICustomizationSettingsDialog } from "../ui-customization-settings-dialog/bindUICustomizationSettingsDialog";
import UICustomizationSettingsDialog from "../ui-customization-settings-dialog/UICustomizationSettingsDialog";
import { useUICustomizationSettingsDialogState } from "../ui-customization-settings-dialog/useUICustomizationSettingsDialogState";
import NavigationViewGroupedListSectionV2 from "./NavigationViewGroupedListSectionV2";
import NavigationViewListToolbar from "./NavigationViewListToolbar";

export type NavigationViewListV2Props = {
    open: boolean;
    customizationSettings: IUICustomizationSettingsDTO | undefined;
    contextualNavDrawerOpen?: boolean;
    onToggleContextualNavDrawer?: (navigationItem: INavigationItem) => void;
    onSelect?: () => void;
};

export default function NavigationViewListV2({
    open,
    customizationSettings,
    contextualNavDrawerOpen,
    onToggleContextualNavDrawer,
    onSelect
}: NavigationViewListV2Props) {

    const isPhone = isMobile && !isTablet;
    const location = useLocation();
    const routeContext = useRouteContext();
    const navigateTo = useNavigateTo();
    const [offlineState] = useOfflineDetectContext();
    const [userState] = useUserContext();
    const viewID = getViewIDFromPath(location.pathname, UIViewEnum.Group_SummaryListing_Devices);
    const configurationDialog = useUICustomizationSettingsDialogState();
    const [layoutState, layoutDispatch] = useLayoutContext();
    const [openList, setOpenList] = useState<number>(-1);

    const { data } = useQueryNotifications(useBreadcrumb)({
        customerID: userState.customer?.id as number,
        viewID: viewID,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
    });

    function onClick(navigationItem: INavigationItem, route: IRoute, shiftKey: boolean, ctrlKey: boolean) {

        let url: string | undefined;

        let options: NavigateToOptions | undefined;

        if (shiftKey || ctrlKey) {
            options = {
                newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
            }
        }

        switch (navigationItem.objectTypeID) {
            case eLynxObjectTypeEnum.Group:
                url = getGroupRoute(route.viewID ?? UIViewEnum.Group_SummaryListing, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.Device:

                //const viewID = route.viewID === UIViewEnum.Device_ManualEntry && !isPhone ? UIViewEnum.Device_GaugeSheet : UIViewEnum.Device_ManualEntry;

                // navigate to the 
                url = getDeviceRoute(route.viewID ?? UIViewEnum.Device_Details, routeContext.groupID, routeContext.wellID, navigationItem.objectID, isPhone);
                //url = getDeviceRoute(viewID, routeContext.groupID, routeContext.wellID, navigationItem.objectID);

                break;

            case eLynxObjectTypeEnum.Well:
                url = getWellRoute(route.viewID ?? UIViewEnum.Well_Details, routeContext.groupID, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.Tank:
                url = getTankRoute(route.viewID ?? UIViewEnum.Tank_Details, routeContext.groupID, routeContext.deviceID, routeContext.tankBatteryID, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.TankBattery:
                url = getTankBatteryRoute(route.viewID ?? UIViewEnum.TankBattery_Details, routeContext.groupID, navigationItem.objectID);
                break;

            case eLynxObjectTypeEnum.Tag:
                url = getTagRoute(route.viewID ?? UIViewEnum.Tag_Details, routeContext.groupID, routeContext.wellID, routeContext.deviceID, routeContext.tankBatteryID, routeContext.tankID, navigationItem.objectID);
                break;

            default:
                throw new Error(`Unable to navigate to object type: ${navigationItem.objectTypeID.toString()}`);
        }

        if (onSelect) {
            onSelect();
        }

        navigateTo(url, options);
    }

    const navigationItems = data?.breadcrumbItems
        .filter(item => item.objectTypeID !== eLynxObjectTypeEnum.Group || item.objectID === routeContext.groupID);

    return (
        <Stack
            sx={{
                flex: "1 1 auto",
                overflowX: "hidden",
                overflowY: "auto",
            }}
            onMouseLeave={() => {
                setOpenList(-1);
            }}
        >
            <NavigationViewListToolbar
                viewAll={layoutState.viewAll}
                railMode={!open}
                onToggleViewAll={() => layoutDispatch({ type: LayoutContextActionType.ViewAllChanged, payload: !layoutState.viewAll })}
                // onOpenSettings={() => {
                //     configurationDialog.open({
                //         userID: userState.user?.id as number
                //     });
                // }}
            />

            <Divider />

            <List
                disablePadding
                sx={{
                    overflowX: "hidden",
                    overflowY: "auto"
                }}
            >
                {navigationItems?.map((item, index) => {

                    const settings = getCustomizationViewCategorySettings(
                        item.objectTypeID,
                        customizationSettings
                    );

                    return (
                        <NavigationViewGroupedListSectionV2
                            key={index}
                            open={(openList === index) || (item.objectTypeID === routeContext.contextObject?.objectType)}
                            navigationItem={item}
                            customizationSettings={settings}
                            viewAll={layoutState.viewAll}
                            onClick={onClick}
                            onOpen={(open) => setOpenList(open ? index : -1)}
                            railMode={!open}
                            contextualNavDrawerOpen={contextualNavDrawerOpen}
                            onToggleContextualNavDrawer={onToggleContextualNavDrawer}
                        />
                    )
                })}
            </List>

            <UICustomizationSettingsDialog {...bindUICustomizationSettingsDialog(configurationDialog)} />
        </Stack>
    );
}

function getCustomizationViewCategorySettings(
    objectType: eLynxObjectTypeEnum,
    customizationSettings: IUICustomizationSettingsDTO | undefined
): IUICustomizationViewCategorySettingsDTO | undefined {
    return objectType === eLynxObjectTypeEnum.Group
        ? customizationSettings?.navigationDrawer?.viewCategories.group
        : objectType === eLynxObjectTypeEnum.Device
            ? customizationSettings?.navigationDrawer?.viewCategories.device
            : objectType === eLynxObjectTypeEnum.Well
                ? customizationSettings?.navigationDrawer?.viewCategories.well
                : objectType === eLynxObjectTypeEnum.TankBattery
                    ? customizationSettings?.navigationDrawer?.viewCategories.tankBattery
                    : objectType === eLynxObjectTypeEnum.Tank
                        ? customizationSettings?.navigationDrawer?.viewCategories.tank
                        : objectType === eLynxObjectTypeEnum.Tag
                            ? customizationSettings?.navigationDrawer?.viewCategories.tag
                            : undefined;
}